import 'leaflet/dist/leaflet.css';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { MapContainer, ImageOverlay, Polygon, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { useMapEvents } from 'react-leaflet';
import { api, getAccessToken } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type PropertyDetail = {
  id: string;
  name: string;
  role: 'owner' | 'member' | null;
  mapImageUrl: string | null;
  mapImageWidth: number | null;
  mapImageHeight: number | null;
  geometryVerifiedAt: string | null;
  archivedAt: string | null;
  archivedReason: string | null;
  defaultMowCadenceDays: number;
};

type PropertyDetailResponse = {
  property: PropertyDetail;
  ownerSummary: { id: string; email: string; displayName: string };
  memberCount: number;
};

type ZoneSummary = {
  id: string;
  characterId: string;
  name: string;
  geometry: { type: 'polygon'; points: [number, number][] } | null;
  colorState: 'green' | 'amber' | 'red' | 'neutral';
  lastCompletedAt: string | null;
  archivedAt: string | null;
};

const COLOR_MAP: Record<ZoneSummary['colorState'], string> = {
  green: '#22c55e',
  amber: '#fbbf24',
  red: '#ef4444',
  neutral: '#9ca3af',
};

const COLOR_DOT: Record<ZoneSummary['colorState'], string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-400',
  red: 'bg-red-500',
  neutral: 'bg-gray-300',
};

export const Route = createFileRoute('/app/$propertyId/map')({
  component: MapPage,
});

function MapPage() {
  const { propertyId } = Route.useParams();

  const { data: propData, isPending: propPending, error: propError } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => api.get<PropertyDetailResponse>('/v1/properties/' + propertyId),
  });

  const { data: zones, isPending: zonesPending, error: zonesError } = useQuery({
    queryKey: ['zones', propertyId],
    queryFn: () => api.get<ZoneSummary[]>('/v1/properties/' + propertyId + '/zones'),
  });

  const [showUpload, setShowUpload] = useState(false);

  if (propPending || zonesPending) {
    return <p className="text-muted-foreground p-6">Loading…</p>;
  }

  if (propError || zonesError || !propData || !zones) {
    return <p className="text-destructive p-6">Failed to load map data.</p>;
  }

  const { property } = propData;
  const isOwner = property.role === 'owner';

  return (
    <div className="md:p-6">
      {!property.mapImageUrl || showUpload ? (
        <div className="p-6 md:p-0">
          {!property.mapImageUrl && (
            <p className="text-muted-foreground mb-4">No property map uploaded yet.</p>
          )}
          {isOwner ? (
            <UploadSection
              propertyId={propertyId}
              onSuccess={() => setShowUpload(false)}
            />
          ) : (
            <p className="text-muted-foreground">Only the owner can upload a map.</p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {isOwner && (
            <div className="flex items-center gap-3 px-4 pt-4 md:px-0 md:pt-0">
              <Button variant="outline" size="sm" onClick={() => setShowUpload(true)}>
                Replace map
              </Button>
            </div>
          )}
          <PropertyMap
            property={property}
            zones={zones}
            isOwner={isOwner}
          />
        </div>
      )}
    </div>
  );
}

function UploadSection({
  propertyId,
  onSuccess,
}: {
  propertyId: string;
  onSuccess: () => void;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleUpload() {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const token = getAccessToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(
        `${(import.meta.env.VITE_API_URL as string | undefined) ?? ''}/v1/properties/${propertyId}/map`,
        { method: 'POST', body: fd, headers, credentials: 'include' },
      );
      if (!res.ok) {
        let message = `Upload failed (${res.status})`;
        try {
          const e = (await res.json()) as { error?: { message?: string } };
          message = e.error?.message ?? message;
        } catch { /* non-JSON error body — keep the status-code message */ }
        throw new Error(message);
      }
      // Ensure the response is JSON before parsing — a 403 blocked by WAF and
      // rewritten to HTML by CloudFront's custom_error_response would arrive
      // here as 200 + HTML, not a JSON API response.
      const ct = res.headers.get('content-type') ?? '';
      if (!ct.includes('application/json')) {
        throw new Error('Upload was blocked — image may be too large or the request was rejected by a security rule.');
      }
      // Update the property cache directly from the upload response so the
      // map renders immediately — no round-trip, no window where mapImageUrl
      // is still null while a background refetch is in flight.
      const body = (await res.json()) as { data: { property: PropertyDetail } };
      const existing = qc.getQueryData<PropertyDetailResponse>(['property', propertyId]);
      qc.setQueryData<PropertyDetailResponse>(['property', propertyId], {
        property: { ...(existing?.property ?? {} as PropertyDetail), ...body.data.property },
        ownerSummary: existing?.ownerSummary ?? { id: '', email: '', displayName: '' },
        memberCount: existing?.memberCount ?? 0,
      });
      void qc.invalidateQueries({ queryKey: ['zones', propertyId] });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 max-w-sm">
      <ul className="text-sm text-muted-foreground list-disc list-inside space-y-0.5">
        <li>JPEG, PNG, WebP, TIFF, or other common image formats</li>
        <li>Minimum 512 × 512 px</li>
        <li>Maximum 8 MB</li>
      </ul>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          setError(null);
          if (f && f.size > 8 * 1024 * 1024) {
            setError('Image must be under 8 MB.');
            setFile(null);
            return;
          }
          setFile(f);
        }}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          Choose file
        </Button>
        <span className="text-sm text-muted-foreground truncate">
          {file ? file.name : 'No file chosen'}
        </span>
      </div>
      {error && <p className="text-destructive text-sm">{error}</p>}
      <Button onClick={handleUpload} disabled={!file || uploading} size="sm">
        {uploading ? 'Uploading…' : 'Upload'}
      </Button>
    </div>
  );
}

function MapInteraction({ onMapClick }: { onMapClick: (latlng: L.LatLng) => void }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

function AutoFit({ bounds }: { bounds: L.LatLngBoundsExpression }) {
  const map = useMap();
  useEffect(() => {
    // Skip fitting at 0×0 and invalidateSize before fitting; paired with
    // minZoom < 0 on the map so fitBounds can zoom out to fit an image larger
    // than the container (see the dashboard map for the same fix).
    const fit = () => {
      const c = map.getContainer();
      if (c.clientWidth === 0 || c.clientHeight === 0) return;
      map.invalidateSize(false);
      map.fitBounds(bounds, { padding: [8, 8], animate: false });
    };
    fit();
    const raf = requestAnimationFrame(fit);
    const ro = new ResizeObserver(() => fit());
    ro.observe(map.getContainer());
    map.on('resize', fit);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      map.off('resize', fit);
    };
  }, [map, bounds]);
  return null;
}

function PropertyMap({
  property,
  zones,
  isOwner,
}: {
  property: PropertyDetail;
  zones: ZoneSummary[];
  isOwner: boolean;
}) {
  const { propertyId } = Route.useParams();
  const qc = useQueryClient();

  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [editingZoneId, setEditingZoneId] = useState<string | null>(null);
  const [draftPoints, setDraftPoints] = useState<[number, number][]>([]);
  const [zonesCollapsed, setZonesCollapsed] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setEditingZoneId(null);
        setDraftPoints([]);
      }
      if (e.key === 'Delete' && editingZoneId) {
        setDraftPoints((pts) => pts.slice(0, -1));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingZoneId]);

  const confirmMutation = useMutation({
    mutationFn: () => api.post<PropertyDetail>('/v1/properties/' + propertyId + '/map/confirm-geometry'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['property', propertyId] }),
  });

  const saveGeometryMutation = useMutation({
    mutationFn: (id: string) =>
      api.put<ZoneSummary>('/v1/zones/' + id + '/geometry', {
        geometry: { type: 'polygon', points: draftPoints },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['zones', propertyId] });
      await qc.invalidateQueries({ queryKey: ['property', propertyId] });
      setEditingZoneId(null);
      setDraftPoints([]);
    },
  });

  const clearGeometryMutation = useMutation({
    mutationFn: (zoneId: string) => api.delete<void>('/v1/zones/' + zoneId + '/geometry'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zones', propertyId] }),
  });

  const imageWidth = property.mapImageWidth ?? 800;
  const imageHeight = property.mapImageHeight ?? 600;
  const mapBounds: L.LatLngBoundsExpression = [[0, 0], [-imageHeight, imageWidth]];

  function handleMapClick(latlng: L.LatLng) {
    if (!editingZoneId) return;
    setDraftPoints((prev) => [...prev, [latlng.lng, -latlng.lat]]);
  }

  const activeZones = zones.filter((z) => !z.archivedAt);

  const zoneListContent = (
    <>
      {activeZones.length === 0 && (
        <p className="text-muted-foreground text-xs">No zones.</p>
      )}
      {activeZones.map((zone) => (
        <div
          key={zone.id}
          className={cn(
            'rounded border border-border px-3 py-2 text-sm cursor-pointer transition-colors',
            zone.id === selectedZoneId || zone.id === editingZoneId
              ? 'bg-blue-50 border-blue-300 dark:bg-blue-950 dark:border-blue-700'
              : 'bg-card hover:bg-muted',
          )}
          onClick={() => setSelectedZoneId(zone.id)}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs bg-muted px-1 rounded text-muted-foreground">
              {zone.characterId}
            </span>
            <div className={cn('w-2.5 h-2.5 rounded-full', COLOR_DOT[zone.colorState])} />
            <span className="flex-1 truncate font-medium text-xs">{zone.name}</span>
          </div>
          {isOwner && (
            <div className="flex gap-1 mt-1">
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setZonesCollapsed(false);
                  setEditingZoneId(zone.id);
                  setDraftPoints(zone.geometry?.points ?? []);
                  setSelectedZoneId(null);
                }}
              >
                Edit
              </Button>
              {zone.geometry && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-xs px-2 text-destructive hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Clear the mapped area for "${zone.name}"? This cannot be undone.`)) return;
                    clearGeometryMutation.mutate(zone.id);
                  }}
                  disabled={clearGeometryMutation.isPending}
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>
      ))}

      {editingZoneId && (
        <div className="flex gap-2 mt-2 pt-2 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => {
              setEditingZoneId(null);
              setDraftPoints([]);
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="flex-1"
            disabled={draftPoints.length < 3 || saveGeometryMutation.isPending}
            onClick={() => saveGeometryMutation.mutate(editingZoneId)}
          >
            Save
          </Button>
        </div>
      )}
    </>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col md:flex-row gap-4">
        <div
          className="w-full md:flex-1 rounded-none md:rounded-lg border-y md:border border-border overflow-hidden isolate"
          style={{ aspectRatio: `${imageWidth} / ${imageHeight}`, maxHeight: '70vh' }}
        >
          <MapContainer
            crs={L.CRS.Simple}
            bounds={mapBounds}
            zoomSnap={0}
            minZoom={-5}
            style={{ width: '100%', height: '100%' }}
          >
            <AutoFit bounds={mapBounds} />
            <ImageOverlay url={property.mapImageUrl!} bounds={mapBounds} />
            <MapInteraction onMapClick={handleMapClick} />

            {activeZones.filter((z) => z.geometry).map((zone) => {
              const positions = zone.geometry!.points.map(
                ([x, y]) => [-y, x] as L.LatLngExpression,
              );
              const isSelected = zone.id === selectedZoneId || zone.id === editingZoneId;
              return (
                <Polygon
                  key={zone.id}
                  positions={positions}
                  pathOptions={{
                    color: isSelected ? '#3b82f6' : COLOR_MAP[zone.colorState],
                    fillOpacity: isSelected ? 0.4 : 0.2,
                    weight: isSelected ? 3 : 2,
                  }}
                  eventHandlers={{ click: () => setSelectedZoneId(zone.id) }}
                >
                  <Tooltip permanent direction="center" className="font-mono text-xs">
                    {zone.characterId}
                  </Tooltip>
                </Polygon>
              );
            })}

            {editingZoneId && draftPoints.length >= 2 && (
              <Polyline
                positions={draftPoints.map(([x, y]) => [-y, x] as L.LatLngExpression)}
                pathOptions={{ color: '#3b82f6', dashArray: '6 4' }}
              />
            )}

            {editingZoneId &&
              draftPoints.map(([x, y], i) => (
                <CircleMarker
                  key={i}
                  center={[-y, x]}
                  radius={6}
                  pathOptions={{ color: '#3b82f6', fillColor: '#fff', fillOpacity: 1 }}
                  eventHandlers={{
                    contextmenu: (e) => {
                      e.originalEvent.preventDefault();
                      setDraftPoints((pts) => pts.filter((_, j) => j !== i));
                    },
                  }}
                />
              ))}
          </MapContainer>
        </div>

        {/* Zone list: sidebar on desktop, hidden here on mobile (shown below) */}
        <div className={cn('hidden md:flex flex-col gap-2 shrink-0', zonesCollapsed ? 'w-8' : 'w-56')}>
          <div className="flex items-center">
            {!zonesCollapsed && (
              <h3 className="text-sm font-semibold flex-1">
                Zones{activeZones.length > 0 && ` (${activeZones.length})`}
              </h3>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 shrink-0 text-muted-foreground"
              onClick={() => setZonesCollapsed((v) => !v)}
              disabled={!!editingZoneId}
              title={zonesCollapsed ? 'Show zones' : 'Hide zones'}
            >
              {zonesCollapsed ? '›' : '‹'}
            </Button>
          </div>
          {!zonesCollapsed && zoneListContent}
        </div>
      </div>

      {/* Zone list: below map on mobile */}
      <div className="md:hidden flex flex-col gap-2 px-4">
        <h3 className="text-sm font-semibold">
          Zones{activeZones.length > 0 && ` (${activeZones.length})`}
        </h3>
        {zoneListContent}
      </div>

      {isOwner && property.geometryVerifiedAt === null && (
        <div className="flex items-center gap-3 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-700 mx-4 md:mx-0 px-4 py-3">
          <span className="text-sm text-amber-800 dark:text-amber-200 flex-1">
            Geometry has not been verified for this property.
          </span>
          <Button
            size="sm"
            onClick={() => confirmMutation.mutate()}
            disabled={confirmMutation.isPending}
          >
            Confirm geometry
          </Button>
        </div>
      )}
    </div>
  );
}
